// Settings: themes, accessibility, voice/screen-time controls, reminders,
// consent, privacy & compliance, data export/delete and the activity ledger.
import { ScrollText } from "lucide-react";
import { Badge } from "../../ui/badge";
import { ThemeSettings } from "../ThemeSettings";
import { ConsentRenewal } from "../ConsentRenewal";
import { VoiceUsageGuard } from "../VoiceUsageGuard";
import { ReminderSettings } from "../../ReminderSettings";
import { BossReminderControls } from "../BossReminderControls";
import { RecapControls } from "../RecapControls";
import { ParentDataControls } from "../ParentDataControls";
import { PrivacyHub } from "../PrivacyHub";
import { ActivityRatingsPanel } from "../../ActivityRatingsPanel";

const ACTION_LABELS = {
  "auth.register": "Account created",
  "auth.login": "Signed in",
  "auth.logout": "Signed out",
  "child.create": "Child profile created",
  "child.update": "Child profile updated",
  "child.delete": "Child profile deleted",
  "consent.grant": "Consent granted",
  "consent.revoke": "Consent withdrawn",
  "child_shell.access": "Child mode opened",
  "ai.story.generate": "AI story generated",
  "ai.story.fallback": "Fallback story served",
  "ai.story.validation": "Story safety check",
  "ai.story.report": "Story reported",
  "ai.illustration.generate": "Story picture created",
  "ai.companion.message": "Coach chat message",
  "ai.companion.input": "Chat input blocked",
  "ai.companion.escalation": "Chat safety escalation",
  "email.recap_sent": "Weekly recap emailed",
  "recap.settings": "Recap settings changed",
};

export const SettingsSection = ({ audit }) => (
  <div className="space-y-8" data-testid="dash-section-settings">
    <div>
      <h1 className="text-2xl sm:text-3xl font-extrabold font-display">Settings</h1>
      <p className="text-muted-foreground mt-1">
        Themes, voice controls, reminders, consent, privacy and your data.
      </p>
    </div>
    <div className="space-y-4">
      <ThemeSettings />
      <ConsentRenewal />
      <VoiceUsageGuard />
      <ReminderSettings />
      <BossReminderControls />
      <RecapControls />
      <ParentDataControls />
      <PrivacyHub />
      <ActivityRatingsPanel />
    </div>

    <section>
      <h2 className="text-base md:text-lg font-bold font-display flex items-center gap-2 mb-4">
        <ScrollText className="h-5 w-5 text-secondary" aria-hidden="true" />{" "}
        Family activity ledger
      </h2>
      <div className="bg-card rounded-2xl border border-border divide-y divide-border"
        data-testid="family-audit-list">
        {audit.length === 0 && (
          <p className="p-5 text-sm text-muted-foreground">No activity recorded yet.</p>
        )}
        {audit.slice(0, 12).map((e) => (
          <div key={e.event_id} className="p-4 flex items-center justify-between gap-4">
            <div>
              <p className="text-sm font-semibold">{ACTION_LABELS[e.action] || e.action}</p>
              <p className="text-xs text-muted-foreground">{new Date(e.ts).toLocaleString()}</p>
            </div>
            <Badge variant={e.decision === "allow" ? "secondary" : "destructive"}
              className="shrink-0">
              {e.decision === "allow"
                ? "Allowed"
                : `Blocked${e.reason ? ` · ${e.reason.replaceAll("_", " ").toLowerCase()}` : ""}`}
            </Badge>
          </div>
        ))}
      </div>
      <p className="text-xs text-muted-foreground mt-3">
        Every consent decision and access to your child's space is recorded
        here. This ledger cannot be edited or deleted.
      </p>
    </section>
  </div>
);
